import React, { useEffect, useState, lazy, Suspense, useCallback } from "react";
import Lenis from "lenis";
import { frame, cancelFrame } from "framer-motion";
import { scrollYMV, scrollProgressMV } from "@/lib/scrollMotion";
import { scrollToTarget } from "@/lib/scroll";
import { Navigation } from "@/components/Navigation";
import { KineticHero } from "@/components/KineticHero";
import { IntroBanner } from "@/components/IntroBanner";
import { BackgroundDecor } from "@/components/primitives/BackgroundDecor";

const PracticeAreasGrid = lazy(() => import("@/components/PracticeAreasGrid").then((m) => ({ default: m.PracticeAreasGrid })));
const EditorialMarquee = lazy(() => import("@/components/EditorialMarquee").then((m) => ({ default: m.EditorialMarquee })));
const InteractiveRoadmap = lazy(() => import("@/components/InteractiveRoadmap").then((m) => ({ default: m.InteractiveRoadmap })));
const ManifestoChapters = lazy(() => import("@/components/ManifestoChapters").then((m) => ({ default: m.ManifestoChapters })));
const AboutFounder = lazy(() => import("@/components/AboutFounder").then((m) => ({ default: m.AboutFounder })));
const FieldWorkGallery = lazy(() => import("@/components/FieldWorkGallery").then((m) => ({ default: m.FieldWorkGallery })));
const ClientLogos = lazy(() => import("@/components/ClientLogos").then((m) => ({ default: m.ClientLogos })));
const ConsultationBookingForm = lazy(() => import("@/components/ConsultationBookingForm").then((m) => ({ default: m.ConsultationBookingForm })));
const ExecutiveFooter = lazy(() => import("@/components/ExecutiveFooter").then((m) => ({ default: m.ExecutiveFooter })));

export default function SiteHome() {
  const [presetService, setPresetService] = useState(null);

  useEffect(() => {
    // Single lerp-based smoothing driven by Framer Motion's frame loop. Lenis's scroll value is
    // pushed into shared MotionValues here, synchronously, so scroll-linked parallax derives from
    // the exact rendered scroll position each frame — no read/update phase race, no jitter.
    const lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
    window.__lenis = lenis;
    const onScroll = ({ scroll, limit }) => {
      scrollYMV.set(scroll);
      scrollProgressMV.set(limit > 0 ? scroll / limit : 0);
    };
    lenis.on("scroll", onScroll);
    const update = (data) => lenis.raf(data.timestamp);
    frame.update(update, true);
    return () => {
      cancelFrame(update);
      lenis.off("scroll", onScroll);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const goBook = useCallback((service) => {
    if (typeof service === "string") setPresetService(service);
    setTimeout(() => scrollToTarget("#book"), 60);
  }, []);

  return (
    <div className="grain relative min-h-screen bg-ink text-foreground">
      <BackgroundDecor />
      <Navigation />
      <main className="relative z-10">
        <IntroBanner onBook={goBook} />
        <KineticHero onBook={goBook} />
        <Suspense fallback={<div className="h-40" />}>
          <PracticeAreasGrid onBook={goBook} />
          <EditorialMarquee />
          <InteractiveRoadmap />
          <ManifestoChapters />
          <AboutFounder />
          <FieldWorkGallery />
          <ClientLogos />
          <ConsultationBookingForm presetService={presetService} />
          <ExecutiveFooter />
        </Suspense>
      </main>
    </div>
  );
}
